import { passkeyClient } from "@better-auth/passkey/client";
import { createAuthClient } from "better-auth/react";
import { emailOTPClient } from "better-auth/client/plugins";
import { API_URL } from "./api";

/**
 * Client di Better Auth.
 *
 * `baseURL` punta all'API, non al frontend: in sviluppo sono due origini
 * diverse (`:5173` e `:3000`). Il cookie di sessione viaggia lo stesso perché
 * `localhost` con porte diverse resta **same-site** — ma solo se ogni richiesta
 * usa `credentials: "include"`, che il client imposta da sé.
 *
 * Attenzione: `localhost` e `127.0.0.1` sono host diversi per i cookie.
 * Mescolarli fa fallire l'accesso in modo silenzioso.
 */
export const authClient = createAuthClient({
  baseURL: API_URL,
  basePath: "/api/auth",
  plugins: [emailOTPClient(), passkeyClient()],
  fetchOptions: {
    credentials: "include",
  },
});

export const { useSession, signOut } = authClient;

/** Estrae un messaggio leggibile dall'errore restituito dal client. */
export function authErrorMessage(error: { message?: string; code?: string } | null): string {
  if (!error) return "Qualcosa è andato storto. Riprova.";

  switch (error.code) {
    case "INVALID_OTP":
    case "INVALID_OR_EXPIRED_OTP":
      return "Codice non valido o scaduto.";
    case "TOO_MANY_ATTEMPTS":
      return "Troppi tentativi. Richiedi un nuovo codice.";
    case "TOO_MANY_REQUESTS":
      return "Troppe richieste ravvicinate. Aspetta un minuto.";
    case "CHALLENGE_NOT_FOUND":
    case "FAILED_TO_VERIFY_REGISTRATION":
      return "Registrazione della passkey non riuscita. Riprova.";
    default:
      return error.message ?? "Qualcosa è andato storto. Riprova.";
  }
}

/**
 * `true` se il browser sa gestire le passkey.
 *
 * Va verificato prima di mostrare qualunque bottone: su un browser senza
 * WebAuthn un pulsante «Entra con passkey» porterebbe solo a un errore.
 */
export function supportsPasskeys(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof navigator.credentials?.get === "function"
  );
}

/**
 * `true` se il browser sa proporre le passkey dentro il campo di testo
 * (conditional UI). Safari e Chrome recenti sì, altri no: senza questo
 * controllo la chiamata in autofill fallisce in silenzio.
 */
export async function supportsPasskeyAutofill(): Promise<boolean> {
  if (!supportsPasskeys()) return false;

  const available = window.PublicKeyCredential?.isConditionalMediationAvailable;
  if (typeof available !== "function") return false;

  try {
    return await available.call(window.PublicKeyCredential);
  } catch {
    return false;
  }
}
